import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import Button from '../components/ui/Button';
import AuthLayout from '../components/AuthLayout';
import { useLogin } from '../hooks/useAuth';
import { useLanguage } from '../lang/LangContext';
import { KEYS } from '../lang/keys';
import { normalizeFieldErrors } from '../services/api';

const Login = () => {
    const { t } = useLanguage();
    const [searchParams, setSearchParams] = useSearchParams();
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        remember: false
    });
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({ email: '', password: '' });
    const [wasReset] = useState(() => searchParams.get('reset') === '1');
    const [loading, setLoading] = useState(false);
    const login = useLogin();

    useEffect(() => {
        if (searchParams.get('reset') === '1') {
            setSearchParams({}, { replace: true });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({ ...prev, [name]: '' }));
        }
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setFieldErrors({ email: '', password: '' });
        setLoading(true);

        try {
            await login.mutateAsync(formData);
            setFormData({ email: '', password: '', remember: false });
        } catch (err) {
            if (err.code === 'VALIDATION_ERROR' && err.errors) {
                setFieldErrors(normalizeFieldErrors(err.errors));
            } else {
                setError(err.message || t(KEYS.LOGIN_ERROR_GENERIC));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout subtitle={t(KEYS.LOGIN_TAGLINE)}>
            <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Email */}
                        <div className="space-y-1.5">
                            <label htmlFor="email" className="label-base">
                                {t(KEYS.COMMON_EMAIL)}
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                className={`input-base ${fieldErrors.email ? 'border-red-500 focus:ring-red-500' : ''}`}
                                placeholder="you@example.com"
                                disabled={loading}
                            />
                            {fieldErrors.email && <span className="text-xs text-red-500 mt-1">{fieldErrors.email}</span>}
                        </div>

                        {/* Password */}
                        <div className="space-y-1.5">
                            <label htmlFor="password" className="label-base">
                                {t(KEYS.COMMON_PASSWORD)}
                            </label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                required
                                className={`input-base ${fieldErrors.password ? 'border-red-500 focus:ring-red-500' : ''}`}
                                placeholder="••••••••"
                                disabled={loading}
                            />
                            {fieldErrors.password && <span className="text-xs text-red-500 mt-1">{fieldErrors.password}</span>}
                            <div className="text-right">
                                <Link to="/forgot-password" className="text-xs text-text-muted hover:text-accent transition-colors">
                                    {t(KEYS.LOGIN_FORGOT_PASSWORD)}
                                </Link>
                            </div>
                        </div>

                        {/* Remember me */}
                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="remember"
                                name="remember"
                                checked={formData.remember}
                                onChange={handleChange}
                                className="w-4 h-4 rounded border-surface-border accent-accent cursor-pointer"
                                disabled={loading}
                            />
                            <label htmlFor="remember" className="text-sm text-text-secondary cursor-pointer select-none">
                                {t(KEYS.LOGIN_REMEMBER_ME)}
                            </label>
                        </div>

                        {/* Success */}
                        {wasReset && (
                            <div className="p-3 bg-status-paid/10 text-status-paid rounded-md text-sm border border-status-paid/20 animate-appear">
                                {t(KEYS.LOGIN_RESET_SUCCESS)}
                            </div>
                        )}

                        {/* Error */}
                        {error && (
                            <div className="p-3 bg-status-overdue-bg text-status-overdue rounded-md text-sm border border-status-overdue/20 animate-appear">
                                {error}
                            </div>
                        )}

                        {/* Submit */}
                        <Button 
                            type="submit" 
                            variant="primary"
                            fullWidth
                            disabled={loading}
                        >
                            {loading ? t(KEYS.LOGIN_SUBMITTING) : t(KEYS.LOGIN_SUBMIT)}
                        </Button>
                    </form>

                    {/* Register Link */}
                    <div className="mt-5 pt-5 border-t border-surface-border text-center">
                        <p className="text-sm text-text-muted">
                            {t(KEYS.LOGIN_NO_ACCOUNT)}{' '}
                            <Link to="/register" className="text-accent hover:text-accent-hover font-medium">
                                {t(KEYS.LOGIN_CREATE_ONE)}
                            </Link>
                        </p>
                    </div>
        </AuthLayout>
    );
};

export default Login;
